<template>
  <!--
    รอบ 2: บริหารวงเงินเหลือจ่ายระดับประเทศ (govem_compensation_central.php: record_approval / round2_preview / round2_confirm / round2_cancel)
    ขั้นตอน: (1) บันทึกการอนุมัติผลรอบ 1 จากผู้มีอำนาจ → (2) คำนวณ/ดูผลรอบ 2 → (3) ยืนยันรอบ 2 ตามคำสั่ง → บันทึกลงทะเบียนประวัติรวมรอบ 2
  -->
  <v-card outlined class="mt-4 round2-card">
    <v-card-title class="subtitle-1 font-weight-bold py-2">
      <v-icon left color="teal darken-2">mdi-cash-refund</v-icon>
      รอบ 2: บริหารวงเงินเหลือจ่าย (หลังผู้มีอำนาจอนุมัติผลรอบ 1)
      <v-spacer />
      <v-chip v-if="run.round2_status === 'final'" small color="teal" dark>ยืนยันรอบ 2 แล้ว</v-chip>
    </v-card-title>
    <v-card-text>
      <v-alert type="info" dense text class="caption">
        วงเงินร้อยละ {{ +run.budget_pct }} เป็นวงเงินของส่วนราชการ — เงินที่เหลือจากการปัดเศษของแต่ละหน่วยงานรวมเป็นกองกลาง
        แล้ว<strong>เติมอัตรากลางจากล่างขึ้นบน</strong>: หาอัตรากลางขั้นต่ำระดับประเทศ k* ที่ใช้เงินเหลือได้มากที่สุด หน่วยงานที่อัตรากลางต่ำกว่า k*
        (วงเงินตึง) ได้ยกขึ้นเป็น k* · หน่วยงานที่สูงกว่าไม่เปลี่ยน · ไม่มีใครถูกลด · ไม่เกินร้อยละ {{ +run.max_pct }}/ขั้นสูงกลุ่มงาน ·
        ผู้ถูกกำหนดร้อยละรายคนไม่ร่วมรอบ 2 · เก็บเป็นยอดเพิ่มแยกจากรอบ 1 (ไม่แก้ผลที่ ผอ. รับรองแล้ว) และรวมเมื่อบันทึกลงทะเบียนประวัติ
      </v-alert>

      <!-- (1) การอนุมัติรอบ 1 -->
      <div class="overline">1. การอนุมัติผลรอบ 1 จากผู้มีอำนาจ</div>
      <div v-if="run.approval_order_no && !editApproval" class="body-2 mb-3">
        <v-icon small color="success">mdi-check-circle</v-icon>
        {{ run.approval_order_no }} ลงวันที่ {{ run.approval_date }} · ผู้อนุมัติ {{ run.approval_authority }}
        <span class="caption grey--text">(บันทึกเมื่อ {{ run.approval_recorded_at }})</span>
        <v-btn v-if="run.round2_status !== 'final'" x-small text color="primary" @click="startEditApproval">แก้ไข</v-btn>
      </div>
      <v-row v-else dense class="mb-1">
        <v-col cols="12" md="4"><v-text-field v-model.trim="approval.approval_order_no" dense outlined label="เลขที่หนังสือ/คำสั่งอนุมัติ *" /></v-col>
        <v-col cols="12" md="3"><v-text-field v-model="approval.approval_date" type="date" dense outlined label="ลงวันที่ *" /></v-col>
        <v-col cols="12" md="3"><v-text-field v-model.trim="approval.approval_authority" dense outlined label="ผู้มีอำนาจอนุมัติ *" /></v-col>
        <v-col cols="12" md="2"><v-btn block color="primary" :loading="busy === 'approval'" @click="saveApproval">บันทึก</v-btn></v-col>
      </v-row>

      <!-- (2)(3) คำนวณและยืนยัน -->
      <template v-if="run.approval_order_no">
        <div class="overline">2. คำนวณการจัดสรรเงินเหลือจ่าย</div>
        <div v-if="run.round2_status === 'final'" class="body-2 mb-2">
          อัตรากลางขั้นต่ำระดับประเทศ k* = <strong>{{ k(run.round2_k) }}</strong> ·
          เงินเหลือรอบ 1 {{ money(run.round2_pool, 2) }} บาท/เดือน · ใช้รอบ 2 <strong>{{ money(run.round2_used) }}</strong> บาท/เดือน
          ({{ money(run.round2_used * 12) }} บาท/ปี) · {{ money(run.round2_persons) }} คน ·
          คำสั่ง {{ run.round2_order_no }} {{ run.round2_order_date ? "ลว. " + run.round2_order_date : "" }} ·
          ยืนยันโดย {{ run.round2_by_name }} {{ run.round2_at }}
          <v-btn x-small outlined color="error" class="ml-2" :disabled="!!run.applied_at" :loading="busy === 'cancel'" @click="cancel">
            ยกเลิกรอบ 2
          </v-btn>
        </div>
        <v-btn v-if="run.round2_status !== 'final'" small color="teal" dark :loading="busy === 'preview'" :disabled="!!run.applied_at" @click="preview">
          <v-icon small left>mdi-calculator</v-icon>คำนวณรอบ 2
        </v-btn>
        <span v-if="run.applied_at" class="caption error--text ml-2">บันทึกลงทะเบียนประวัติแล้ว — ยกเลิกการบันทึกก่อนดำเนินการรอบ 2</span>

        <div v-if="result" class="mt-3">
          <v-row dense>
            <v-col v-for="t in tiles" :key="t.label" cols="6" md="2">
              <v-card outlined class="pa-2 text-center">
                <div class="caption grey--text">{{ t.label }}</div>
                <div class="subtitle-1 font-weight-bold" :class="t.cls">{{ t.value }}</div>
              </v-card>
            </v-col>
          </v-row>
          <v-alert v-if="result.summary.all_capped" type="success" dense text class="caption mt-2">
            เงินเหลือพอให้ผู้มีสิทธิ์ทุกคน (วิธีอัตรากลาง) ได้เต็มเพดานแล้ว — ส่วนที่เหลือไม่จัดสรรเพิ่ม
          </v-alert>
          <v-data-table :headers="headers" :items="result.colleges.filter(c => c.round2_amount > 0 || showAll)" dense class="mt-2"
            :items-per-page="10" :footer-props="{ 'items-per-page-options': [10, 25, 100, -1] }">
            <template v-slot:[`item.factor_k`]="{ item }">{{ k(item.factor_k) }} → <strong>{{ k(item.k_after) }}</strong></template>
            <template v-slot:[`item.leftover`]="{ item }">{{ money(item.leftover, 2) }}</template>
            <template v-slot:[`item.round2_amount`]="{ item }"><strong>{{ money(item.round2_amount) }}</strong></template>
          </v-data-table>
          <v-checkbox v-model="showAll" dense hide-details label="แสดงหน่วยงานที่ไม่ได้รับเพิ่มด้วย" class="mt-0" />

          <template v-if="run.round2_status !== 'final'">
            <div class="overline mt-3">3. ยืนยันรอบ 2 ตามคำสั่งผู้มีอำนาจ</div>
            <v-row dense>
              <v-col cols="12" md="4"><v-text-field v-model.trim="confirmForm.order_no" dense outlined label="เลขที่คำสั่ง/หนังสืออนุมัติรอบ 2 *" /></v-col>
              <v-col cols="12" md="3"><v-text-field v-model="confirmForm.order_date" type="date" dense outlined label="ลงวันที่" /></v-col>
              <v-col cols="12" md="3"><v-text-field v-model="confirmForm.note" dense outlined label="หมายเหตุ" /></v-col>
              <v-col cols="12" md="2">
                <v-btn block color="teal" dark :loading="busy === 'confirm'" :disabled="!result.summary.persons" @click="confirmRound2">ยืนยันรอบ 2</v-btn>
              </v-col>
            </v-row>
          </template>
        </div>
      </template>
    </v-card-text>
    <v-snackbar v-model="snack.show" :color="snack.color" top :timeout="6000">{{ snack.text }}</v-snackbar>
  </v-card>
</template>

<script>
const API = "govem_compensation_central.php";

export default {
  name: "CompensationRound2",
  props: { run: { type: Object, required: true } },
  data() {
    return {
      ApiKey: "HRvec2021",
      busy: null,
      result: null,
      showAll: false,
      editApproval: false,
      approval: { approval_order_no: "", approval_date: "", approval_authority: "" },
      confirmForm: { order_no: "", order_date: new Date().toISOString().slice(0, 10), note: "" },
      snack: { show: false, text: "", color: "success" },
      headers: [
        { text: "หน่วยงาน", value: "college_name" },
        { text: "อัตรากลาง k (รอบ 1 → รอบ 2)", value: "factor_k" },
        { text: "เงินเหลือรอบ 1 (บาท/ด.)", value: "leftover", align: "right" },
        { text: "ได้เพิ่ม (คน)", value: "round2_persons", align: "right" },
        { text: "เงินเพิ่มรอบ 2 (บาท/ด.)", value: "round2_amount", align: "right" }
      ]
    };
  },
  computed: {
    tiles() {
      const s = this.result.summary;
      return [
        { label: "วงเงินทั้งประเทศ (บาท/ด.)", value: this.money(s.national_pool, 2) },
        { label: "ใช้รอบ 1", value: this.money(s.round1_used) },
        { label: "เงินเหลือจ่าย", value: this.money(s.leftover, 2), cls: "warning--text text--darken-2" },
        { label: "k* ขั้นต่ำระดับประเทศ", value: this.k(s.k) },
        { label: "ใช้รอบ 2", value: `${this.money(s.used)} (${this.money(s.persons)} คน)`, cls: "teal--text" },
        { label: "คงเหลือหลังรอบ 2", value: this.money(s.remaining, 2) }
      ];
    }
  },
  watch: {
    "run.run_id"() {
      this.result = null;
      if (this.run.round2_status === "final") this.preview();
    }
  },
  mounted() {
    if (this.run.round2_status === "final") this.preview();
  },
  methods: {
    money(v, d = 0) {
      return Number(v || 0).toLocaleString("th-TH", { minimumFractionDigits: d, maximumFractionDigits: d });
    },
    k(v) {
      return v === null || v === undefined ? "-" : Number(v).toFixed(4);
    },
    notify(text, color = "success") {
      this.snack = { show: true, text, color };
    },
    async api(payload) {
      try {
        const res = await this.$http.post(API, { ApiKey: this.ApiKey, run_id: this.run.run_id, ...payload });
        if (!res.data || res.data.status !== true) throw new Error((res.data && res.data.message) || "ไม่ได้รับข้อมูล");
        return res.data;
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message;
        throw new Error(msg || e.message);
      }
    },
    startEditApproval() {
      this.approval = {
        approval_order_no: this.run.approval_order_no || "",
        approval_date: this.run.approval_date || "",
        approval_authority: this.run.approval_authority || ""
      };
      this.editApproval = true;
    },
    async saveApproval() {
      this.busy = "approval";
      try {
        const r = await this.api({ action: "record_approval", ...this.approval });
        this.editApproval = false;
        this.$emit("changed", r.run);
        this.notify("บันทึกการอนุมัติผลรอบ 1 แล้ว");
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.busy = null;
      }
    },
    async preview() {
      this.busy = "preview";
      try {
        this.result = await this.api({ action: "round2_preview" });
      } catch (e) {
        // หลังยืนยันรอบ 2 แล้ว preview ใช้แสดงผลการกระจาย — ถ้าไม่ได้ก็ไม่ต้องแจ้ง
        if (this.run.round2_status !== "final") this.notify(e.message, "error");
      } finally {
        this.busy = null;
      }
    },
    async confirmRound2() {
      const s = this.result.summary;
      if (!confirm(`ยืนยันรอบ 2: เพิ่มให้ ${s.persons} คน รวม ${this.money(s.used)} บาท/เดือน?\nระบบตรวจสอบความถูกต้องจะคำนวณซ้ำ และรวมยอดเมื่อบันทึกลงทะเบียนประวัติ`)) return;
      this.busy = "confirm";
      try {
        const r = await this.api({ action: "round2_confirm", ...this.confirmForm });
        this.$emit("changed", r.run);
        this.notify("ยืนยันรอบ 2 แล้ว");
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.busy = null;
      }
    },
    async cancel() {
      if (!confirm("ยกเลิกรอบ 2? ยอดเพิ่มรอบ 2 ของทุกคนจะถูกล้าง")) return;
      this.busy = "cancel";
      try {
        const r = await this.api({ action: "round2_cancel" });
        this.result = null;
        this.$emit("changed", r.run);
        this.notify("ยกเลิกรอบ 2 แล้ว", "info");
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.busy = null;
      }
    }
  }
};
</script>

<style scoped>
.round2-card {
  border-left: 4px solid #00897b !important;
}
</style>
